import type { ReactNode } from 'react';
import { chipColours } from '../../lib/colour';
import { useTheme } from '../../theme/ThemeProvider';
import { formatCount } from './project-tickets';
import type { BoardColumn } from './lane-columns';

/**
 * A column header, drawn once above all the swimlanes.
 *
 * ⭐ A lane header carries an expand control — that is the whole point of six columns: `Development`
 * opens into its six statuses when someone actually needs them, and closes again. ⛔ The 26 columns
 * are reachable but never the default.
 */
export function ColumnHeader({
  column,
  count,
  countIsFloor,
  expanded,
  onToggleLane,
}: {
  column: BoardColumn;
  count: number;
  countIsFloor: boolean;
  expanded: boolean;
  onToggleLane: (lane: string) => void;
}) {
  const { resolved } = useTheme();
  const colours = chipColours(column.colour, resolved === 'dark');
  const canExpand = column.kind === 'lane' && column.statuses.length > 1;

  return (
    <div data-testid="column-header" className="flex min-w-0 items-center gap-1.5 px-2 py-1.5">
      {column.kind === 'status' ? (
        <span
          aria-hidden="true"
          style={{ backgroundColor: colours.background }}
          className="size-2 shrink-0 rounded-full"
        />
      ) : null}

      <span
        className="min-w-0 truncate text-[11px] font-bold tracking-wide text-[color:var(--color-ink-muted)] uppercase"
        title={column.kind === 'lane' ? `${column.label} — ${column.statuses.length} statuses` : column.label}
      >
        {column.label}
      </span>

      <span
        title={countIsFloor ? 'At least this many — the board shows a window of the project' : undefined}
        className="shrink-0 rounded bg-[color:var(--color-line)] px-1 text-[10px] font-semibold"
      >
        {formatCount(count, !countIsFloor || count === 0)}
      </span>

      <span className="flex-1" />

      {canExpand ? (
        <button
          type="button"
          onClick={() => onToggleLane(column.lane)}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${column.label} into its ${column.statuses.length} statuses`}
          className="shrink-0 rounded px-1 text-xs text-[color:var(--color-ink-muted)] hover:bg-black/5 dark:hover:bg-white/10"
        >
          {expanded ? '⌄' : '›'}
        </button>
      ) : null}

      {column.kind === 'status' ? (
        <button
          type="button"
          onClick={() => onToggleLane(column.lane)}
          aria-label={`Collapse ${column.laneLabel} back to one column`}
          className="shrink-0 rounded px-1 text-xs text-[color:var(--color-ink-muted)] hover:bg-black/5 dark:hover:bg-white/10"
        >
          ⌃
        </button>
      ) : null}
    </div>
  );
}

/** The droppable body of one column within one swimlane. */
export function ColumnDropZone({
  column,
  droppable,
  refusalReason,
  onDrop,
  children,
}: {
  column: BoardColumn;
  /** Computed from the API's own transition list — see `drag-rules.ts`. */
  droppable: boolean | null;
  /** Why not, when a drag is in flight and this column will not take it. */
  refusalReason: string | null;
  onDrop: () => void;
  children: ReactNode;
}) {
  const dragging = droppable !== null;

  return (
    <div
      data-testid="board-column"
      data-column-key={column.key}
      aria-label={column.label}
      title={dragging && !droppable && refusalReason ? refusalReason : undefined}
      onDragOver={(event) => {
        // ⛔ THE GATE. `preventDefault` is what makes a drop possible at all, so NOT calling it is how
        // an illegal target refuses one — the cursor says no before anything is sent to the API.
        if (!droppable) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(event) => {
        if (!droppable) return;
        event.preventDefault();
        onDrop();
      }}
      className={`flex min-h-24 min-w-0 flex-col gap-2 rounded-md p-2 transition-colors ${
        dragging && droppable
          ? 'bg-sky-100/70 outline-2 outline-dashed outline-sky-500 dark:bg-sky-950/40'
          : 'bg-[color:var(--color-surface)]'
      } ${dragging && !droppable ? 'opacity-50' : ''}`}
    >
      {children}
    </div>
  );
}
